import {
  Business as BusinessIcon,
  Factory as FactoryIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Box, Chip, Grid, Tooltip, Typography } from "@mui/material";
import React from "react";
import { EntityDetailResponse } from "../../../../types/entityTypes";
import { formatProjectVolume } from "../../../../utils/numberFormatting";
import ProjectStatusChip from "../../../common/ProjectStatusChip";

interface ProjectDetailProps {
  detailedProject: EntityDetailResponse;
  onEntityClick?: (entityId: string) => void;
}

// Function to get project status color
const getProjectStatusColor = (
  status: string
):
  | "success"
  | "error"
  | "warning"
  | "info"
  | "primary"
  | "secondary"
  | "default" => {
  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case "in_progress":
    case "ongoing":
      return "success";
    case "completed":
      return "primary";
    case "cancelled":
    case "failed":
      return "error";
    case "delayed":
    case "paused":
    case "suspended":
    case "stalled":
      return "warning";
    case "planned":
    case "proposed":
      return "default";
    case "pending":
      return "info";
    case "alleged":
      return "secondary";
    default:
      return "default";
  }
};

const ProjectDetail: React.FC<ProjectDetailProps> = ({
  detailedProject,
  onEntityClick,
}) => {
  return (
    <Box sx={{ p: 0, flexShrink: 0 }}>
      <Grid container spacing={3}>
        {/* Project Overview Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", mb: 3 }}
            >
              Project Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Project Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {detailedProject?.p?.project_status ? (
                    Array.isArray(detailedProject.p.project_status) ? (
                      detailedProject.p.project_status.map((status, index) => (
                        <ProjectStatusChip
                          key={index}
                          statusId={status}
                          lastUpdatedDate={detailedProject.p?.publication_date}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))
                    ) : (
                      <ProjectStatusChip
                        statusId={detailedProject.p.project_status}
                        lastUpdatedDate={detailedProject.p?.publication_date}
                        size="small"
                      />
                    )
                  ) : (
                    <Chip label="-" color="default" size="small" />
                  )}
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Last Update
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedProject?.p?.publication_date || "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedProject?.p?.project_start || "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  End Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedProject?.p?.project_end || "-"}
                </Typography>
              </Grid>
              {detailedProject?.p?.project_volume_value && (
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Project Volume
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatProjectVolume(
                      detailedProject?.p?.project_volume_value,
                      detailedProject?.p?.project_volume_currency
                    )}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Project Countries Section */}
            {detailedProject?.p?.project_countries &&
              detailedProject.p.project_countries.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Project Countries
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {detailedProject.p.project_countries
                      .slice(0, 5)
                      .map((country: string, index: number) => (
                        <Chip
                          key={index}
                          label={country}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: "background.paper",
                            borderColor: "divider",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                        />
                      ))}
                    {detailedProject.p.project_countries.length > 5 && (
                      <Tooltip
                        title={
                          <Box>
                            {detailedProject.p.project_countries
                              .slice(5)
                              .map((country, idx) => (
                                <div key={idx}>{country}</div>
                              ))}
                          </Box>
                        }
                      >
                        <Chip
                          label={`+${detailedProject.p.project_countries.length - 5}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: "action.hover",
                            borderColor: "divider",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
          </Box>
        </Grid>

        {/* Project Participants Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", mb: 3 }}
            >
              Project Participants
            </Typography>
            <Grid container spacing={2}>
              {/* Individual Organization Tiles */}
              {detailedProject?.organizations?.map((orgItem, index) => (
                <Grid item xs={6} key={`org-${index}`}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #444",
                      borderRadius: 1,
                      backgroundColor: "#1a1a1a",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <BusinessIcon sx={{ fontSize: 16 }} />
                      Organization
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() =>
                        onEntityClick?.(orgItem.organization?.id || "")
                      }
                    >
                      {orgItem.organization?.name || "Unknown Organization"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      gutterBottom
                    >
                      Role:{" "}
                      {orgItem.relationship?.semantic_role ||
                        orgItem.relationship?.relation_type ||
                        "Participant"}
                    </Typography>
                    {orgItem.relationship?.project_role && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontSize: "0.75rem", lineHeight: 1.3 }}
                      >
                        {orgItem.relationship.project_role}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}

              {/* Individual Facility Tiles */}
              {detailedProject?.facilities?.map((facilityItem, index) => (
                <Grid item xs={6} key={`facility-${index}`}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #444",
                      borderRadius: 1,
                      backgroundColor: "#1a1a1a",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <FactoryIcon sx={{ fontSize: 16 }} />
                      Facility
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() =>
                        onEntityClick?.(facilityItem.facility?.id || "")
                      }
                    >
                      {facilityItem.facility?.name || "Unknown Facility"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      gutterBottom
                    >
                      Role:{" "}
                      {facilityItem.relationship?.semantic_role ||
                        facilityItem.relationship?.relation_type ||
                        "Participant"}
                    </Typography>
                    {facilityItem.relationship?.project_role && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontSize: "0.75rem", lineHeight: 1.3 }}
                      >
                        {facilityItem.relationship.project_role}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}

              {/* Individual People Tiles */}
              {detailedProject?.p?.related_people?.map((person, index) => (
                <Grid item xs={6} key={`person-${index}`}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #444",
                      borderRadius: 1,
                      backgroundColor: "#1a1a1a",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <PersonIcon sx={{ fontSize: 16 }} />
                      Person
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                      sx={{
                        cursor: person.entity_id ? "pointer" : "default",
                        "&:hover": person.entity_id
                          ? { textDecoration: "underline" }
                          : {},
                      }}
                      onClick={() =>
                        person.entity_id && onEntityClick?.(person.entity_id)
                      }
                    >
                      {person.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      gutterBottom
                    >
                      Role: {person.project_role}
                    </Typography>
                    {person.project_role_description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontSize: "0.75rem", lineHeight: 1.3 }}
                      >
                        {person.project_role_description}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}

              {/* Show message if no participants */}
              {(!detailedProject?.organizations ||
                detailedProject.organizations.length === 0) &&
                (!detailedProject?.facilities ||
                  detailedProject.facilities.length === 0) &&
                (!detailedProject?.p?.related_people ||
                  detailedProject.p.related_people.length === 0) && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        No project participants found
                      </Typography>
                    </Box>
                  </Grid>
                )}
            </Grid>
          </Box>
        </Grid>

        {/* Industry Classification Section */}
        {(detailedProject?.p?.naics_codes_with_title?.length ?? 0) > 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Industry Classification
              </Typography>
              {/* NAICS Codes */}
              {detailedProject?.p?.naics_codes_with_title &&
                detailedProject.p.naics_codes_with_title.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      NAICS Industry Codes:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedProject.p.naics_codes_with_title
                        .slice(0, 6)
                        .map((code, index) => (
                          <Chip
                            key={index}
                            label={code}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedProject.p.naics_codes_with_title.length > 6 && (
                        <Tooltip
                          title={
                            <Box>
                              {detailedProject.p.naics_codes_with_title
                                .slice(6)
                                .map((code, idx) => (
                                  <div key={idx}>{code}</div>
                                ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
                          >
                            +
                            {detailedProject.p.naics_codes_with_title.length -
                              6}{" "}
                            more
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ProjectDetail;
